export interface FuzzyMatchOptions {
    /** Minimum query length before the fuzzy fallback is attempted. */
    minFuzzyQueryLength?: number;
    /** Queries up to this length use the lower edit distance threshold. */
    shortQueryMaxLength?: number;
    /** Queries longer than this skip the fuzzy fallback entirely. */
    fuzzyFallbackMaxLength?: number;
    /** Maximum edit distance allowed for queries up to shortQueryMaxLength chars. */
    shortQueryEditThreshold?: number;
    /** Maximum edit distance allowed for queries between shortQueryMaxLength+1 and fuzzyFallbackMaxLength chars. */
    longQueryEditThreshold?: number;
}

/**
 * Returns true if `query` matches `target` — either as a case-insensitive substring,
 * or (for short queries) within a Levenshtein edit-distance threshold.
 *
 * Thresholds and cut-offs are configurable via `options`
 */
export function fuzzyMatch(query: string, target: string, options: FuzzyMatchOptions = {}): boolean {
    const {
        minFuzzyQueryLength = 3,
        shortQueryMaxLength = 4,
        fuzzyFallbackMaxLength = 8,
        shortQueryEditThreshold = 1,
        longQueryEditThreshold = 2,
    } = options;

    if (!query || query.trim() === '') {
        return true;
    }

    const normalisedQuery = query.trim().toLowerCase();
    const normalisedTarget = target.toLowerCase();

    if (normalisedTarget.includes(normalisedQuery)) {
        return true;
    }

    if (normalisedQuery.length < minFuzzyQueryLength
        || normalisedQuery.length > fuzzyFallbackMaxLength) {
        return false;
    }

    const editThreshold = normalisedQuery.length <= shortQueryMaxLength
        ? shortQueryEditThreshold
        : longQueryEditThreshold;

    for (let i = 0; i <= normalisedTarget.length - normalisedQuery.length; i++) {
        const window = normalisedTarget.substring(i, i + normalisedQuery.length);
        if (levenshtein(normalisedQuery, window) <= editThreshold) {
            return true;
        }
    }

    return false;
}

function levenshtein(source: string, candidate: string): number {
    const distances = Array.from({ length: source.length + 1 }, (_, row) =>
        Array.from({ length: candidate.length + 1 }, (_, col) => initialEditDistance(row, col))
    );

    for (let row = 1; row <= source.length; row++) {
        for (let col = 1; col <= candidate.length; col++) {
            distances[row][col] = source[row - 1] === candidate[col - 1]
                ? distances[row - 1][col - 1]
                : 1 + Math.min(distances[row - 1][col], distances[row][col - 1], distances[row - 1][col - 1]);
        }
    }

    return distances[source.length][candidate.length];
}

function initialEditDistance(row: number, col: number): number {
    if (row === 0) return col;
    if (col === 0) return row;
    return 0;
}

